import { Menu } from '@base-ui/react/menu'
import { twMerge } from 'tailwind-merge'
import type { PartProps } from '../../../lib/part-props'

/**
 * Props interface for MenuBackdrop component.
 * Extends base-ui's Menu.Backdrop.Props for backdrop functionality.
 */
interface MenuBackdropProps extends PartProps<Menu.Backdrop.Props> {
  className?: string
}

/**
 * MenuBackdrop - An overlay displayed beneath the menu popup.
 *
 * This component renders a semi-transparent backdrop behind the menu
 * when it's open. It's typically used for modal-style menus where you
 * want to dim or block interaction with the rest of the page.
 *
 * Features:
 * - Semi-transparent overlay
 * - Fade in/out animations
 * - Blocks interaction with page content
 * - Closes menu when clicked (default behavior)
 *
 * Note: Most menus don't need a backdrop. This is primarily used for
 * modal menus on mobile devices or when you want to focus attention
 * on the menu.
 *
 * @example
 * ```tsx
 * <Menu modal>
 *   <Menu.Trigger>Open</Menu.Trigger>
 *   <Menu.Portal>
 *     <Menu.Backdrop />
 *     <Menu.Positioner>
 *       <Menu.Popup>
 *         <Menu.Item>Action</Menu.Item>
 *       </Menu.Popup>
 *     </Menu.Positioner>
 *   </Menu.Portal>
 * </Menu>
 * ```
 */
function MenuBackdrop({ className, ...props }: MenuBackdropProps) {
  return (
    <Menu.Backdrop
      className={twMerge(
        // Base styles
        'fixed inset-0',
        // Background color with transparency
        'bg-backdrop-subtle',
        // Transitions
        'transition-opacity',
        // Animation states
        'data-[starting-style]:opacity-0',
        'data-[ending-style]:opacity-0',
        className,
      )}
      {...props}
    />
  )
}

export { MenuBackdrop }
